import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MatTabsModule } from "@angular/material/tabs";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { BehaviorSubject, Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { PageHeaderComponent } from "../../shared/page-header/page-header.component";
import { ProductionRequestService } from "../../services/production-request.service";
import { UploadService } from "../../services/upload.service";
import { SnackbarService } from "../../services/snackbar.service";
import { ProductTypeSelectorComponent } from "../../shared/product-type-selector/product-type-selector.component";
import { Talent } from "../../models/talent.model";
import { TalentService } from "../../services/talent.service";
import { PermissionService } from "../../services/permission.service";
import { ProfileService } from "../../services/profile.service";
import { getDefaultVoiceActorName } from "../../utils/default-voice-actor.util";
import { COMMERCIAL_AUDIO_ERROR, isCommercialAudioFile } from "../../utils/audio-file";
import { MAX_SPOT_AUDIO_SECONDS, exceedsMaxSpotAudioSeconds } from "../../utils/spot-length";
import { parseBlob } from "music-metadata-browser";

@Component({
  selector: "app-create-commercial",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    PageHeaderComponent,
    ProductTypeSelectorComponent,
  ],
  templateUrl: "./create-commercial.component.html",
  styleUrls: ["./create-commercial.component.css"],
})
export class CreateCommercialComponent implements OnInit {
  requestId: number | null = null;
  requestStatus: string | null = null;
  allowCreate = true;

  uploadFile: File | null = null;
  uploadFileUrl: string | null = null;
  uploadFileError = "";
  isUploadingFile = false;
  uploadFileProgress = 0;
  audioDuration = 0;
  recordingActualDuration = 0;

  isRecording = false;
  recordingBlob: Blob | null = null;
  recordingUrl: string | null = null;
  showRecordingPlayer = false;
  recordingLength = 60;
  secondsLeft = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private timer: any;
  private stream: MediaStream | null = null;
  nextAudioVersion: number = 1;

  submitting = false;
  selectedProductTypeIds: number[] = [];
  productTypeError = "";
  readonly accept = "audio/mpeg,audio/mp3,audio/webm,audio/wav";

  selectedTalent: Talent | null = null;
  talentInput = "";
  talents: Talent[] = [];
  filteredTalents$: Observable<Talent[]> = new Observable();
  private talentInput$ = new BehaviorSubject<string>("");
  private talentPrefillDismissed = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productionRequestService: ProductionRequestService,
    private uploadService: UploadService,
    private cdr: ChangeDetectorRef,
    private snackbar: SnackbarService,
    private talentService: TalentService,
    private permissionService: PermissionService,
    private profileService: ProfileService,
  ) { }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get("id");
    if (idParam) this.requestId = +idParam;
    if (this.requestId) this.loadRequest();
    this.loadTalents();
    this.setupTalentFilters();
    this.prefillDefaultVoiceActor();
  }

  loadRequest(): void {
    if (!this.requestId) return;
    this.productionRequestService.getById(this.requestId).subscribe({
      next: (data) => {
        this.requestStatus = data?.request?.status ?? null;
        this.allowCreate = this.requestStatus !== "audio_uploaded" && this.requestStatus !== "approved";
        this.nextAudioVersion = data?.next_audio_version ?? 1;
      },
      error: () => {
        this.allowCreate = false;
      },
    });
  }

  private loadTalents(): void {
    this.talentService.getTalents().subscribe({
      next: (talents) => {
        this.talents = talents;
      },
      error: (err) => {
        console.error("[CreateCommercial] Error loading talents:", err);
      },
    });
  }

  private setupTalentFilters(): void {
    this.filteredTalents$ = this.talentInput$.pipe(
      startWith(""),
      map((input) => {
        const searchTerm = input.toLowerCase().trim();
        if (!searchTerm) return this.talents;
        return this.talents.filter((t) => t.name.toLowerCase().includes(searchTerm));
      }),
    );
  }

  private prefillDefaultVoiceActor(): void {
    if (this.talentPrefillDismissed) return;

    const defaultName = getDefaultVoiceActorName(
      this.permissionService.getCurrentRole(),
      this.profileService.getCurrentUser(),
    );
    if (!defaultName) return;

    this.talentService.resolveOrCreateTalentByName(defaultName).subscribe({
      next: (talent) => {
        if (this.talentPrefillDismissed) return;
        this.selectedTalent = talent;
        this.talentInput = talent.name;
        this.talentInput$.next(talent.name);
        if (talent.id != null && !this.talents.some((t) => t.id === talent.id)) {
          this.talents.push(talent);
        }
      },
      error: (err) => {
        console.error("[CreateCommercial] Error pre-filling voice actor:", err);
      },
    });
  }

  onTalentInputChange(value: string): void {
    this.talentInput = value;
    this.talentInput$.next(value);
  }

  displayTalentName(talent: Talent): string {
    return talent ? talent.name : "";
  }

  onTalentSelectionChange(talent: Talent): void {
    this.selectedTalent = talent;
    this.talentInput = talent.name;
  }

  isTalentExisting(): boolean {
    return this.talents.some((t) => t.name.toLowerCase() === this.talentInput.toLowerCase());
  }

  async createTalent(): Promise<Talent | null> {
    if (!this.talentInput.trim()) return null;
    const newTalent: Talent = { name: this.talentInput.trim(), description: "Voice actor" };
    try {
      const created = await this.talentService.createTalent(newTalent).toPromise();
      if (created) {
        this.talents.push(created);
        this.selectedTalent = created;
        this.talentInput = created.name;
        return created;
      }
    } catch (error) {
      console.error("[CreateCommercial] Error creating talent:", error);
      this.snackbar.error("Failed to create talent. Please try again.");
    }
    return null;
  }

  removeSelectedTalent(): void {
    this.selectedTalent = null;
    this.talentInput = "";
    this.talentInput$.next("");
    this.talentPrefillDismissed = true;
  }

  onUploadFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file && !isCommercialAudioFile(file)) {
      this.rejectCommercialFile(input);
      return;
    }
    this.assignFile(file);
  }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && !isCommercialAudioFile(file)) {
      this.rejectCommercialFile(null);
      return;
    }
    this.assignFile(file || null);
  }

  private rejectCommercialFile(input: HTMLInputElement | null): void {
    if (input) input.value = "";
    this.uploadFile = null;
    if (this.uploadFileUrl) {
      URL.revokeObjectURL(this.uploadFileUrl);
      this.uploadFileUrl = null;
    }
    this.audioDuration = 0;
    this.uploadFileError = COMMERCIAL_AUDIO_ERROR;
    this.snackbar.warning(COMMERCIAL_AUDIO_ERROR);
  }

  onDragOver(e: DragEvent): void {
    e.preventDefault();
  }

  private assignFile(file: File | null | undefined): void {
    if (!file) return;
    if (!isCommercialAudioFile(file)) {
      this.rejectCommercialFile(null);
      return;
    }
    this.uploadFileError = "";
    this.uploadFile = file;
    this.uploadFileUrl = URL.createObjectURL(file);
    this.audioDuration = 0;
    void this.readUploadDuration(file).then((duration) => {
      if (this.uploadFile === file) this.audioDuration = duration;
    });
  }

  /**
   * Length from the file itself (same parser as the recording upload), not an <audio>
   * metadata event, which may not have fired by submit time (Chrome defers media loading in
   * background tabs) and then sent no length at all.
   */
  private async readUploadDuration(file: File): Promise<number> {
    try {
      const metadata = await parseBlob(file);
      const duration = metadata.format.duration || 0;
      if (isFinite(duration) && duration > 0) return duration;
    } catch (err) {
      console.error("Failed to read audio metadata:", err);
    }
    return 0;
  }

  /** Same bounds as the backend and the recording upload (1..65s; >65s cannot be scheduled). */
  private audioLengthError(duration: number): string {
    if (!isFinite(duration) || duration < 1) return "Could not read the audio length (minimum 1 second).";
    if (exceedsMaxSpotAudioSeconds(duration)) return `Audio is too long (maximum ${MAX_SPOT_AUDIO_SECONDS} seconds).`;
    return "";
  }

  clearUpload(): void {
    this.uploadFile = null;
    if (this.uploadFileUrl) {
      URL.revokeObjectURL(this.uploadFileUrl);
      this.uploadFileUrl = null;
    }
    this.uploadFileError = "";
    this.productTypeError = "";
  }

  onProductTypeSelectionChange(ids: number[]): void {
    this.selectedProductTypeIds = ids;
    if (ids.length > 0) {
      this.productTypeError = "";
    }
  }

  private validateProductTypes(): boolean {
    if (this.selectedProductTypeIds.length > 0) {
      this.productTypeError = "";
      return true;
    }
    this.productTypeError = "Please assign at least one product type";
    return false;
  }

  private async ensureSelectedTalent(): Promise<Talent | null> {
    if (this.selectedTalent) return this.selectedTalent;

    if (this.talentInput.trim() && !this.isTalentExisting()) {
      const created = await this.createTalent();
      if (created) return created;
    }

    if (this.talentInput.trim() && this.isTalentExisting()) {
      const match = this.talents.find((t) => t.name.toLowerCase() === this.talentInput.toLowerCase());
      if (match) {
        this.selectedTalent = match;
        return match;
      }
    }

    this.snackbar.warning("Please select a voice actor or add a new one.");
    return null;
  }

  async submitUpload(): Promise<void> {
    if (!this.requestId || !this.uploadFile) return;
    if (!isCommercialAudioFile(this.uploadFile)) {
      this.uploadFileError = COMMERCIAL_AUDIO_ERROR;
      this.snackbar.warning(COMMERCIAL_AUDIO_ERROR);
      return;
    }
    if (!this.validateProductTypes()) return;

    const talent = await this.ensureSelectedTalent();
    if (!talent?.id) return;

    const uploadFile = this.uploadFile;
    const duration = this.audioDuration > 0 ? this.audioDuration : await this.readUploadDuration(uploadFile);
    const lengthError = this.audioLengthError(duration);
    if (lengthError) {
      this.snackbar.warning(lengthError);
      return;
    }

    this.uploadFileError = "";
    this.submitting = true;

    const requestId = this.requestId;
    const version = this.nextAudioVersion;
    this.uploadService
      .uploadFile(this.uploadFile, "production_requests", { request_id: this.requestId, version: version })
      .subscribe({
        next: (result) => {
          this.productionRequestService
            // Fractions kept: the length picks the rate tier (34.6s must not become 35s).
            .registerAudio(requestId, result.tmp_key, duration, this.selectedProductTypeIds, talent.id)
            .subscribe({
              next: () => {
                this.submitting = false;
                this.selectedProductTypeIds = [];
                this.snackbar.success("Commercial audio uploaded successfully.");
                this.router.navigate(["/production-requests", requestId]);
              },
              error: (err) => {
                this.submitting = false;
                const msg = err?.error?.error || "Failed to register audio.";
                this.uploadFileError = msg;
                this.snackbar.error(msg);
              },
            });
        },
        error: () => {
          this.submitting = false;
          this.uploadFileError = "Upload failed. Please try again.";
          this.snackbar.error("Upload failed. Please try again.");
        },
      });
  }

  startRecording(): void {
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      this.stream = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.audioChunks = [];
      this.mediaRecorder.ondataavailable = (e) => e.data.size && this.audioChunks.push(e.data);
      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        this.recordingBlob = new Blob(this.audioChunks, { type: mimeType });
        this.recordingUrl = URL.createObjectURL(this.recordingBlob);
        this.showRecordingPlayer = true;
        this.isRecording = false;
        this.recordingActualDuration = this.recordingLength - this.secondsLeft;
        this.cdr.detectChanges();
      };
      this.mediaRecorder.start();
      this.isRecording = true;
      this.secondsLeft = this.recordingLength;
      this.timer = setInterval(() => {
        this.secondsLeft--;
        if (this.secondsLeft <= 0) this.stopRecording();
      }, 1000);
    }).catch(() => {
      this.uploadFileError = "Microphone access is required to record.";
    });
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  clearRecording(): void {
    this.recordingBlob = null;
    if (this.recordingUrl) {
      URL.revokeObjectURL(this.recordingUrl);
      this.recordingUrl = null;
    }
    this.showRecordingPlayer = false;
    this.secondsLeft = 0;
    this.productTypeError = "";
  }

  async submitRecording(): Promise<void> {
    if (!this.requestId || !this.recordingBlob) return;
    if (!this.validateProductTypes()) return;

    const talent = await this.ensureSelectedTalent();
    if (!talent?.id) return;

    const recordingError = this.audioLengthError(this.recordingActualDuration);
    if (recordingError) {
      this.snackbar.warning(recordingError);
      return;
    }

    const file = new File([this.recordingBlob], "recording.webm", {
      type: this.recordingBlob.type,
    });

    this.uploadFileError = "";
    this.submitting = true;

    const requestId = this.requestId;

    this.uploadService
      .uploadFile(file, "production_requests", { request_id: this.requestId, version: this.nextAudioVersion })
      .subscribe({
        next: (result) => {
          this.productionRequestService
            .registerAudio(requestId, result.tmp_key, this.recordingActualDuration, this.selectedProductTypeIds, talent.id)
            .subscribe({
              next: () => {
                this.submitting = false;
                this.selectedProductTypeIds = [];
                this.snackbar.success("Commercial recording uploaded successfully.");
                this.router.navigate(["/production-requests", requestId]);
              },
              error: (err) => {
                this.submitting = false;
                const msg = err?.error?.error || "Failed to register audio.";
                this.uploadFileError = msg;
                this.snackbar.error(msg);
              },
            });
        },
        error: () => {
          this.submitting = false;
          this.uploadFileError = "Upload failed. Please try again.";
          this.snackbar.error("Upload failed. Please try again.");
        },
      });
  }

  get timerDisplay(): string {
    const m = Math.floor(Math.max(0, this.secondsLeft) / 60).toString().padStart(2, "0");
    const s = (Math.max(0, this.secondsLeft) % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }
}
